// ─── Role-aware in-app help content ──────────────────────────────────────────
// Plain-language how-to guides, authored as data so they are easy to edit and
// extend without touching UI components. Each article declares the roles it is
// relevant to; the Help page and the context Help panel filter by the logged-in
// user's role so a lower role never sees higher-role topics.
//
// Roles mirror the tenant role model (src/types/index.ts `UserRole` +
// DEFAULT_ROLE_PERMISSIONS in src/lib/permissions.ts). `owner` and the platform
// `super_admin` always see every article. Owner-only topics (billing, turning
// the online store on & paying, custom domains) omit every other role.
//
// Content is intentionally generic to the app's default role setup. A tenant
// owner can re-configure any non-owner role in Business Settings → Roles &
// Permissions, so a staff member's real access may differ from these defaults.

export type HelpRole =
  | 'owner'
  | 'admin'
  | 'manager'
  | 'cashier'
  | 'accountant'
  | 'sales_person'
  | 'viewer'

export interface HelpArticle {
  /** Stable id — also used as the scroll-to anchor for "Help on this page". */
  id: string
  /** Roles this article is shown to. Owner/super_admin always see everything. */
  roles: HelpRole[]
  /** Grouping heading shown on the Help page. */
  section: string
  title: string
  /** One-line plain summary shown under the title. */
  summary: string
  /** A few short, clear steps. */
  steps: string[]
  /** In-app path this article helps with (drives the "Open …" link and the
   *  "Help on this page" matcher). */
  link?: string
  /** Button label for the link, e.g. "Open POS". */
  linkLabel?: string
  /** Extra search terms not already in the title/steps. */
  keywords?: string[]
}

// Handy role bundles to keep the data readable. Owner is included everywhere for
// clarity even though the filter grants owner all articles regardless.
const EVERYONE: HelpRole[] = ['owner', 'admin', 'manager', 'cashier', 'accountant', 'sales_person', 'viewer']
const OWNER_ADMIN: HelpRole[] = ['owner', 'admin']
const OWNER_ONLY: HelpRole[] = ['owner']

export const HELP_ARTICLES: HelpArticle[] = [
  // ─── Getting started ───────────────────────────────────────────────────────
  {
    id: 'getting-started-welcome',
    roles: EVERYONE,
    section: 'Getting started',
    title: 'Welcome to Palstro',
    summary: 'A quick tour of how the app is organised.',
    steps: [
      'Use the sidebar on the left to move between the main areas of your business.',
      'The big groups are Trading (buying & selling), Finance, Payroll, Business Settings and System.',
      'Tap the menu button at the top-left on a phone or tablet to open the sidebar.',
      'This Help guide only shows the topics for your role, so you only see what applies to you.',
    ],
    link: '/dashboard',
    linkLabel: 'Go to Dashboard',
    keywords: ['start', 'begin', 'intro', 'overview', 'new'],
  },
  {
    id: 'getting-started-dashboard',
    roles: EVERYONE,
    section: 'Getting started',
    title: 'Read your dashboard',
    summary: 'See how your business is doing today at a glance.',
    steps: [
      'Open Dashboard from the top of the sidebar.',
      'The cards show today’s sales, orders, low-stock items and money owed to you.',
      'Tap a card to jump into the full details for that area.',
      'The alerts panel flags anything that needs your attention.',
    ],
    link: '/dashboard',
    linkLabel: 'Go to Dashboard',
    keywords: ['home', 'summary', 'kpi', 'overview'],
  },
  {
    id: 'getting-started-support',
    roles: EVERYONE,
    section: 'Getting started',
    title: 'Get more help',
    summary: 'Reach the Palstro team when this guide isn’t enough.',
    steps: [
      'Open Support to send a question to the Palstro team.',
      'Describe the issue and include the page you were on.',
      'You’ll get replies in the same place, plus a notification.',
    ],
    link: '/support',
    linkLabel: 'Open Support',
    keywords: ['support', 'contact', 'ticket', 'question', 'problem'],
  },

  // ─── Selling & POS ─────────────────────────────────────────────────────────
  {
    id: 'pos-ring-up',
    roles: ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
    section: 'Selling at the till (POS)',
    title: 'Ring up a sale',
    summary: 'Sell to a walk-in customer at the point of sale.',
    steps: [
      'Open POS and, if asked, open your register for the day.',
      'Tap products to add them to the cart, or scan a barcode with a USB scanner.',
      'Adjust quantities in the cart, then choose the customer (or leave as walk-in).',
      'Tap Pay to collect payment and print the receipt.',
    ],
    link: '/pos',
    linkLabel: 'Open POS',
    keywords: ['till', 'checkout', 'cart', 'sell', 'counter'],
  },
  {
    id: 'pos-take-payment',
    roles: ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
    section: 'Selling at the till (POS)',
    title: 'Take cash, card, transfer or pay-on-delivery',
    summary: 'Collect payment and give the right change.',
    steps: [
      'On the payment screen pick the method: Cash, Card, Transfer or Pay on delivery.',
      'For cash, type the amount received — the change is worked out for you.',
      'You can split a sale across more than one method if the customer pays part-cash, part-transfer.',
      'Confirm to finish the sale and print or share the receipt.',
    ],
    link: '/pos',
    linkLabel: 'Open POS',
    keywords: ['cash', 'card', 'transfer', 'pod', 'change', 'payment', 'pay'],
  },
  {
    id: 'pos-find-product',
    roles: ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
    section: 'Selling at the till (POS)',
    title: 'Find a product quickly',
    summary: 'Locate an item by name or barcode.',
    steps: [
      'Type part of the product name in the POS search box.',
      'Or scan the item’s barcode with a USB scanner — it’s added straight to the cart.',
      'Use the Barcode Scanner page to check a product’s price and stock without selling it.',
    ],
    link: '/barcode',
    linkLabel: 'Open Barcode Scanner',
    keywords: ['search', 'barcode', 'scan', 'lookup', 'find item'],
  },
  {
    id: 'pos-register',
    roles: ['owner', 'admin', 'manager', 'cashier'],
    section: 'Selling at the till (POS)',
    title: 'Open and close your cash register',
    summary: 'Start and end your trading session with a cash count.',
    steps: [
      'At the start of your shift, open the register and enter your opening cash float.',
      'Sell as normal through the day.',
      'At the end, close the register and count your cash by denomination.',
      'The app shows any difference between counted and expected cash.',
    ],
    link: '/pos',
    linkLabel: 'Open POS',
    keywords: ['register', 'shift', 'float', 'close', 'session', 'end of day', 'cash count'],
  },
  {
    id: 'pos-discount',
    roles: ['owner', 'admin', 'manager', 'sales_person'],
    section: 'Selling at the till (POS)',
    title: 'Give a discount',
    summary: 'Reduce the price on a sale when allowed.',
    steps: [
      'Add items to the cart as usual.',
      'Tap the discount control and enter an amount or a percentage.',
      'The total updates instantly.',
      'If you can’t see the discount option, your role doesn’t allow discounts — ask a manager.',
    ],
    link: '/pos',
    linkLabel: 'Open POS',
    keywords: ['discount', 'reduce', 'markdown', 'promo', 'off'],
  },

  // ─── Sales & invoices ──────────────────────────────────────────────────────
  {
    id: 'sales-new-invoice',
    roles: ['owner', 'admin', 'manager', 'sales_person'],
    section: 'Sales & invoices',
    title: 'Create a sales invoice',
    summary: 'Raise an invoice for a customer order.',
    steps: [
      'Go to Trading → Sell → Invoices and tap New.',
      'Pick the customer, then add products and quantities.',
      'Adjust the unit price if needed (you can only raise it above the list price).',
      'Choose whether it’s paid now or on credit, then save.',
    ],
    link: '/sales/new',
    linkLabel: 'New invoice',
    keywords: ['invoice', 'order', 'bill', 'credit sale', 'new sale'],
  },
  {
    id: 'sales-track',
    roles: ['owner', 'admin', 'manager', 'accountant', 'cashier', 'sales_person', 'viewer'],
    section: 'Sales & invoices',
    title: 'View and track your sales',
    summary: 'Find invoices and see what’s paid or outstanding.',
    steps: [
      'Open Trading → Sell → Invoices.',
      'Use the status badges to spot unpaid or partly-paid invoices.',
      'Tap any invoice to see its full details and payment history.',
      'Depending on your role you may see only your own sales or everyone’s.',
    ],
    link: '/sell/invoices',
    linkLabel: 'Open Invoices',
    keywords: ['invoices', 'sales list', 'outstanding', 'unpaid', 'history'],
  },
  {
    id: 'sales-add-payment',
    roles: ['owner', 'admin', 'manager', 'sales_person'],
    section: 'Sales & invoices',
    title: 'Record a payment on an invoice',
    summary: 'Log money received against a credit sale.',
    steps: [
      'Open the invoice from Trading → Sell → Invoices.',
      'Tap Add Payment and enter the amount and method.',
      'Save — the outstanding balance updates automatically.',
    ],
    link: '/sell/invoices',
    linkLabel: 'Open Invoices',
    keywords: ['receipt', 'payment', 'part payment', 'settle', 'collect'],
  },
  {
    id: 'sales-return',
    roles: ['owner', 'admin', 'manager'],
    section: 'Sales & invoices',
    title: 'Process a customer return',
    summary: 'Take goods back and refund or credit the customer.',
    steps: [
      'Open the original invoice, or go to Trading → Sell → Returns.',
      'Start a new return and pick the items and quantities coming back.',
      'Choose whether to refund cash or leave a credit on the customer’s account.',
      'Save — stock and the customer’s balance are corrected for you.',
    ],
    link: '/sell/returns',
    linkLabel: 'Open Returns',
    keywords: ['return', 'refund', 'credit note', 'goods back'],
  },
  {
    id: 'sales-share',
    roles: ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
    section: 'Sales & invoices',
    title: 'Share an invoice with a customer',
    summary: 'Send a link a customer can open on their phone.',
    steps: [
      'Open the invoice you want to share.',
      'Tap Share to create a public link.',
      'Copy the link and send it by WhatsApp or SMS.',
      'You can revoke the link later if you need to.',
    ],
    link: '/sell/invoices',
    linkLabel: 'Open Invoices',
    keywords: ['share', 'link', 'whatsapp', 'send invoice'],
  },

  // ─── Customers & suppliers ─────────────────────────────────────────────────
  {
    id: 'contacts-add-customer',
    roles: ['owner', 'admin', 'manager', 'cashier', 'sales_person'],
    section: 'Customers & suppliers',
    title: 'Add a customer',
    summary: 'Save a customer so you can track their orders and balance.',
    steps: [
      'Go to Trading → Contacts.',
      'Tap Add and choose Customer.',
      'Enter their name and phone number (add more details if you have them).',
      'Save — you can now select them on sales and see their balance.',
    ],
    link: '/contacts',
    linkLabel: 'Open Contacts',
    keywords: ['customer', 'client', 'contact', 'add customer'],
  },
  {
    id: 'contacts-add-supplier',
    roles: ['owner', 'admin', 'manager'],
    section: 'Customers & suppliers',
    title: 'Add a supplier',
    summary: 'Save a supplier so you can record purchases from them.',
    steps: [
      'Go to Trading → Contacts and switch to the Suppliers tab.',
      'Tap Add and enter the supplier’s name and contact details.',
      'Save — you can now record purchases and payments to them.',
    ],
    link: '/contacts',
    linkLabel: 'Open Contacts',
    keywords: ['supplier', 'vendor', 'add supplier'],
  },
  {
    id: 'contacts-balance',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Customers & suppliers',
    title: 'Check what a customer owes',
    summary: 'See a customer’s balance and statement.',
    steps: [
      'Open Trading → Contacts and tap the customer.',
      'Their outstanding balance shows at the top.',
      'Open the statement or ledger to see every sale and payment.',
    ],
    link: '/contacts',
    linkLabel: 'Open Contacts',
    keywords: ['balance', 'owes', 'debt', 'statement', 'ledger'],
  },
  {
    id: 'contacts-reminder',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Customers & suppliers',
    title: 'Send a payment reminder',
    summary: 'Nudge a customer who owes you money.',
    steps: [
      'Open the customer in Trading → Contacts.',
      'Tap Send Reminder.',
      'Choose WhatsApp or email and review the message.',
      'Send — the reminder includes their outstanding balance.',
    ],
    link: '/contacts',
    linkLabel: 'Open Contacts',
    keywords: ['reminder', 'chase', 'whatsapp', 'follow up', 'due'],
  },
  {
    id: 'contacts-credit-limit',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Customers & suppliers',
    title: 'Set a customer’s credit limit',
    summary: 'Cap how much a customer can owe on credit.',
    steps: [
      'Go to Trading → Contacts → Credit Limits.',
      'Find the customer and enter their limit.',
      'Save — new credit sales are checked against this cap.',
    ],
    link: '/customers/credit-limits',
    linkLabel: 'Open Credit Limits',
    keywords: ['credit limit', 'cap', 'credit', 'exposure'],
  },

  // ─── Products & inventory ──────────────────────────────────────────────────
  {
    id: 'products-browse',
    roles: ['owner', 'admin', 'manager', 'cashier', 'accountant', 'sales_person', 'viewer'],
    section: 'Products & stock',
    title: 'Browse your products',
    summary: 'See your item list, prices and stock.',
    steps: [
      'Open Trading → Inventory → Products.',
      'Search by name to find an item.',
      'Items low on stock are flagged in amber.',
    ],
    link: '/inventory',
    linkLabel: 'Open Products',
    keywords: ['products', 'items', 'catalogue', 'inventory'],
  },
  {
    id: 'products-add-edit',
    roles: ['owner', 'admin', 'manager'],
    section: 'Products & stock',
    title: 'Add or edit a product',
    summary: 'Create a new item or update an existing one.',
    steps: [
      'Open Trading → Inventory → Products and tap Add (or tap an item to edit).',
      'Enter the name, selling price and (if you track it) the cost price.',
      'Set a low-stock threshold so you get warned before you run out.',
      'Save.',
    ],
    link: '/inventory',
    linkLabel: 'Open Products',
    keywords: ['add product', 'edit product', 'new item', 'price'],
  },
  {
    id: 'products-price',
    roles: ['owner', 'admin', 'manager'],
    section: 'Products & stock',
    title: 'Set or raise a price',
    summary: 'Change what a product sells for.',
    steps: [
      'Open the product in Trading → Inventory → Products.',
      'Update the selling price and save.',
      'At the till or on an invoice you can raise the price above the list price, but not below it — use a discount to go lower.',
    ],
    link: '/inventory',
    linkLabel: 'Open Products',
    keywords: ['price', 'raise price', 'pricing', 'selling price'],
  },
  {
    id: 'stock-levels',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Products & stock',
    title: 'Check stock and low-stock',
    summary: 'See what you have and what’s running out.',
    steps: [
      'Open Trading → Inventory → Low Stock to see items below their re-order level.',
      'Use this list to plan your next purchase.',
    ],
    link: '/inventory/low-stock',
    linkLabel: 'Open Low Stock',
    keywords: ['stock', 'low stock', 'reorder', 'out of stock', 'levels'],
  },
  {
    id: 'stock-adjust',
    roles: ['owner', 'admin', 'manager'],
    section: 'Products & stock',
    title: 'Adjust stock after a count',
    summary: 'Correct stock figures to match a physical count.',
    steps: [
      'Open Trading → Inventory → Stock Entries.',
      'Create an adjustment and pick the location and items.',
      'Enter the correct quantities and a reason, then save.',
    ],
    link: '/stock',
    linkLabel: 'Open Stock Entries',
    keywords: ['adjust', 'stock take', 'count', 'correction', 'shrinkage'],
  },
  {
    id: 'stock-transfer',
    roles: ['owner', 'admin', 'manager'],
    section: 'Products & stock',
    title: 'Move stock between locations',
    summary: 'Transfer items from one shop or warehouse to another.',
    steps: [
      'Open Trading → Inventory → Stock Transfers and tap New.',
      'Choose the from and to locations.',
      'Add the items and quantities to move, then save.',
    ],
    link: '/inventory/transfers',
    linkLabel: 'Open Transfers',
    keywords: ['transfer', 'move stock', 'warehouse', 'branch'],
  },

  // ─── Purchases ─────────────────────────────────────────────────────────────
  {
    id: 'purchase-new',
    roles: ['owner', 'admin', 'manager'],
    section: 'Buying & purchases',
    title: 'Record a purchase from a supplier',
    summary: 'Log stock you bought and update your inventory.',
    steps: [
      'Go to Trading → Buy → Purchases and tap New.',
      'Pick the supplier and add the items and quantities you bought.',
      'Enter the cost prices and whether it’s paid or on credit.',
      'Save — your stock goes up automatically.',
    ],
    link: '/purchases/new',
    linkLabel: 'New purchase',
    keywords: ['purchase', 'buy', 'stock in', 'supplier invoice', 'po'],
  },
  {
    id: 'purchase-receive',
    roles: ['owner', 'admin', 'manager'],
    section: 'Buying & purchases',
    title: 'Receive goods in transit',
    summary: 'Confirm stock has arrived from a supplier.',
    steps: [
      'Open Trading → Buy → Goods in Transit.',
      'Find the order that has arrived and tap Receive.',
      'Confirm the quantities received, then save.',
    ],
    link: '/buy/in-transit',
    linkLabel: 'Open Goods in Transit',
    keywords: ['receive', 'in transit', 'goods', 'delivery', 'grn'],
  },
  {
    id: 'purchase-return',
    roles: ['owner', 'admin', 'manager'],
    section: 'Buying & purchases',
    title: 'Return goods to a supplier',
    summary: 'Send stock back and reduce what you owe.',
    steps: [
      'Open Trading → Buy → Purchase Returns.',
      'Start a return against the original purchase.',
      'Pick the items going back and save — stock and the supplier balance are corrected.',
    ],
    link: '/buy/returns',
    linkLabel: 'Open Purchase Returns',
    keywords: ['purchase return', 'send back', 'supplier refund'],
  },

  // ─── Expenses ──────────────────────────────────────────────────────────────
  {
    id: 'expense-add',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Expenses',
    title: 'Record a business expense',
    summary: 'Log money spent so your accounts stay accurate.',
    steps: [
      'Go to Trading → Expenses and tap Add.',
      'Choose the expense category and enter the amount.',
      'Pick how it was paid (cash or bank) and add a note.',
      'Save.',
    ],
    link: '/expenses',
    linkLabel: 'Open Expenses',
    keywords: ['expense', 'spend', 'cost', 'bill', 'outgoing'],
  },
  {
    id: 'expense-void',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Expenses',
    title: 'Void a posted expense',
    summary: 'Reverse an expense recorded in error.',
    steps: [
      'Open Trading → Expenses and find the entry.',
      'Tap Void and confirm.',
      'The expense is reversed in your accounts.',
    ],
    link: '/expenses',
    linkLabel: 'Open Expenses',
    keywords: ['void', 'reverse', 'cancel expense', 'mistake'],
  },

  // ─── Reports ───────────────────────────────────────────────────────────────
  {
    id: 'reports-sales',
    roles: ['owner', 'admin', 'manager', 'accountant'],
    section: 'Reports',
    title: 'See how sales are going',
    summary: 'Review sales trends over any date range.',
    steps: [
      'Open Finance → Reporting → Sales Analysis.',
      'Pick a date range or a preset like This Week or This Month.',
      'Read the chart and table, and export to a spreadsheet if you need to.',
    ],
    link: '/reports/sales-analysis',
    linkLabel: 'Open Sales Analysis',
    keywords: ['reports', 'sales report', 'trends', 'analysis', 'export'],
  },
  {
    id: 'reports-eod',
    roles: ['owner', 'admin', 'manager', 'cashier'],
    section: 'Reports',
    title: 'Close the day (end of day)',
    summary: 'Reconcile cash and see the day’s takings.',
    steps: [
      'At the end of trading, close your register from POS.',
      'Review the sales summary by payment method.',
      'Count your cash by denomination and check the variance.',
    ],
    link: '/pos',
    linkLabel: 'Open POS',
    keywords: ['end of day', 'eod', 'close', 'cash up', 'reconcile'],
  },
  {
    id: 'reports-financials',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Reports',
    title: 'Profit & Loss and Balance Sheet',
    summary: 'View your core financial statements.',
    steps: [
      'Open Finance → Reporting → Financial Statements.',
      'Choose the period you want to see.',
      'Switch between Profit & Loss, Balance Sheet and Cash Flow.',
    ],
    link: '/reports/financial-statements',
    linkLabel: 'Open Financial Statements',
    keywords: ['profit', 'loss', 'p&l', 'balance sheet', 'financials'],
  },
  {
    id: 'reports-debtors',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Reports',
    title: 'See who owes you money',
    summary: 'Review aged debtors so you can chase overdue accounts.',
    steps: [
      'Open Finance → Reporting → Aged Debtors.',
      'Balances are grouped by how overdue they are.',
      'Use it to prioritise which customers to chase.',
    ],
    link: '/reports/aged-debtors',
    linkLabel: 'Open Aged Debtors',
    keywords: ['aged debtors', 'receivables', 'overdue', 'owing', 'ar'],
  },

  // ─── Accounting & finance ──────────────────────────────────────────────────
  {
    id: 'acct-journal',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Accounting & finance',
    title: 'Post a journal entry',
    summary: 'Make a manual accounting entry.',
    steps: [
      'Open Finance → Accounting → Manual Journal.',
      'Add the accounts to debit and credit so the entry balances.',
      'Add a description and the date, then post.',
    ],
    link: '/accounting/manual-journal',
    linkLabel: 'Open Manual Journal',
    keywords: ['journal', 'debit', 'credit', 'entry', 'gl'],
  },
  {
    id: 'acct-coa',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Accounting & finance',
    title: 'Chart of accounts',
    summary: 'View and manage your account structure.',
    steps: [
      'Open Finance → Accounting → Chart of Accounts.',
      'Browse the account tree by type (assets, liabilities, income, expenses).',
      'Add or edit accounts to fit how you want to report.',
    ],
    link: '/accounting/chart-of-accounts',
    linkLabel: 'Open Chart of Accounts',
    keywords: ['chart of accounts', 'coa', 'accounts', 'ledger accounts'],
  },
  {
    id: 'acct-bank-rec',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Accounting & finance',
    title: 'Reconcile your bank',
    summary: 'Match your records to your bank statement.',
    steps: [
      'Open Finance → Accounting → Bank Reconciliation.',
      'Pick the bank account and the statement period.',
      'Tick off transactions that match, and investigate anything left over.',
    ],
    link: '/accounting/bank-reconciliation',
    linkLabel: 'Open Bank Reconciliation',
    keywords: ['bank', 'reconcile', 'statement', 'match'],
  },
  {
    id: 'acct-tax',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Accounting & finance',
    title: 'File and remit taxes',
    summary: 'Handle VAT, WHT, CIT and PAYE returns.',
    steps: [
      'Open Finance → Tax Compliance.',
      'Pick the tax you need: VAT, WHT, CIT or Payroll (PAYE).',
      'Review the period’s figures and mark the return filed or remitted.',
    ],
    link: '/finance/tax-compliance/vat',
    linkLabel: 'Open Tax Compliance',
    keywords: ['tax', 'vat', 'wht', 'cit', 'paye', 'remit', 'returns'],
  },
  {
    id: 'acct-assets',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Accounting & finance',
    title: 'Track fixed assets & depreciation',
    summary: 'Keep an asset register and post depreciation.',
    steps: [
      'Open Finance → Fixed Assets → Asset Register.',
      'Add each asset with its cost and category.',
      'Run the monthly depreciation from the Depreciation Runs page.',
    ],
    link: '/finance/fixed-assets/register',
    linkLabel: 'Open Asset Register',
    keywords: ['assets', 'depreciation', 'fixed assets', 'register'],
  },

  // ─── Payroll & staff ───────────────────────────────────────────────────────
  {
    id: 'payroll-add-staff',
    roles: OWNER_ADMIN,
    section: 'Payroll & staff',
    title: 'Add a staff member',
    summary: 'Create an employee record for payroll.',
    steps: [
      'Open Payroll → Staff and tap Add.',
      'Enter the employee’s details and their salary.',
      'Save — they’ll be included when you next run payroll.',
    ],
    link: '/payroll/staff',
    linkLabel: 'Open Staff',
    keywords: ['staff', 'employee', 'add staff', 'hire'],
  },
  {
    id: 'payroll-run',
    roles: OWNER_ADMIN,
    section: 'Payroll & staff',
    title: 'Run payroll for the month',
    summary: 'Generate payslips for everyone in one go.',
    steps: [
      'Open Payroll → Run Payroll.',
      'Choose the pay period.',
      'Review the payslips and totals, then process them.',
    ],
    link: '/payroll/run',
    linkLabel: 'Open Run Payroll',
    keywords: ['payroll', 'salary', 'run payroll', 'pay staff', 'payslip'],
  },
  {
    id: 'payroll-payslips',
    roles: ['owner', 'admin', 'accountant'],
    section: 'Payroll & staff',
    title: 'Send payslips',
    summary: 'Share payslips with your employees.',
    steps: [
      'Open Payroll → Payslips.',
      'Find the payslip for the staff member and period.',
      'Email it to the employee, or print it.',
    ],
    link: '/payroll/payslips',
    linkLabel: 'Open Payslips',
    keywords: ['payslip', 'email payslip', 'salary slip'],
  },

  // ─── Online store ──────────────────────────────────────────────────────────
  {
    id: 'store-orders',
    roles: ['owner', 'admin', 'manager'],
    section: 'Online store',
    title: 'Handle online orders',
    summary: 'See and fulfil orders from your storefront.',
    steps: [
      'Open Trading → Sell → Online Orders.',
      'Open a new order to see the items and the customer’s details.',
      'Confirm the order (you can message the customer on WhatsApp) and fulfil it.',
    ],
    link: '/sell/online-orders',
    linkLabel: 'Open Online Orders',
    keywords: ['online orders', 'storefront orders', 'ecommerce', 'web orders'],
  },
  {
    id: 'store-setup',
    roles: OWNER_ADMIN,
    section: 'Online store',
    title: 'Set up your storefront',
    summary: 'Configure your online shop’s details and delivery.',
    steps: [
      'Open Business Settings → Commerce / Storefront.',
      'Set your shop contact details and delivery zones with their fees.',
      'Add your logo and choose your storefront look (theme) and colour.',
    ],
    link: '/business-settings/commerce',
    linkLabel: 'Open Commerce Settings',
    keywords: ['storefront', 'online store', 'delivery zones', 'branding', 'theme', 'logo'],
  },
  {
    id: 'store-activate',
    roles: OWNER_ONLY,
    section: 'Online store',
    title: 'Turn the online store on & pay for it',
    summary: 'Activate the storefront add-on and keep it running.',
    steps: [
      'Open the Online Store page (also reachable from the storefront pill at the top).',
      'Review the add-on price and activate it.',
      'Pay securely with Paystack to switch your shop on.',
      'Renew before it expires so your shop doesn’t go offline.',
    ],
    link: '/online-store',
    linkLabel: 'Open Online Store',
    keywords: ['activate', 'pay', 'subscribe', 'add-on', 'renew store', 'turn on'],
  },
  {
    id: 'store-domain',
    roles: OWNER_ONLY,
    section: 'Online store',
    title: 'Connect your own web address',
    summary: 'Point a custom domain at your storefront.',
    steps: [
      'Open the Online Store page.',
      'Add your domain (for example yourshop.com) in the custom domain section.',
      'Follow the DNS steps shown to verify it.',
      'Once verified, your shop opens on your own web address.',
    ],
    link: '/online-store',
    linkLabel: 'Open Online Store',
    keywords: ['domain', 'custom domain', 'website', 'dns', 'url'],
  },

  // ─── Team, roles & business setup ──────────────────────────────────────────
  {
    id: 'settings-profile',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Update your business details',
    summary: 'Edit your company name, currency and preferences.',
    steps: [
      'Open Business Settings → Business Profile.',
      'Update your business name, address, currency and VAT rate.',
      'Save your changes.',
    ],
    link: '/business-settings/profile',
    linkLabel: 'Open Business Profile',
    keywords: ['business settings', 'company', 'currency', 'profile', 'vat'],
  },
  {
    id: 'settings-users',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Add a team member',
    summary: 'Invite staff and give them a role.',
    steps: [
      'Open Business Settings → Users.',
      'Tap Invite and enter the person’s email.',
      'Choose their role, which decides what they can see and do.',
      'They’ll get an invitation to set their password.',
    ],
    link: '/business-settings/users',
    linkLabel: 'Open Users',
    keywords: ['users', 'team', 'invite', 'staff account', 'add user'],
  },
  {
    id: 'settings-roles',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Control what each role can do',
    summary: 'Fine-tune the permissions for each role.',
    steps: [
      'Open Business Settings → Roles & Permissions.',
      'Pick a role to see and edit its access.',
      'Turn pages and actions on or off, then save.',
      'Some sensitive actions are owner-only by default.',
    ],
    link: '/business-settings/roles',
    linkLabel: 'Open Roles & Permissions',
    keywords: ['roles', 'permissions', 'access', 'restrict', 'security'],
  },
  {
    id: 'settings-locations',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Set up locations & warehouses',
    summary: 'Add the shops and stores you operate from.',
    steps: [
      'Open Business Settings → Locations.',
      'Add each shop or warehouse with its name.',
      'Save — you can then hold stock and sell per location.',
    ],
    link: '/business-settings/locations',
    linkLabel: 'Open Locations',
    keywords: ['locations', 'warehouse', 'branch', 'store', 'shop'],
  },
  {
    id: 'settings-payment-accounts',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Set up cash & bank accounts',
    summary: 'Define where money is received and paid from.',
    steps: [
      'Open Business Settings → Finance Settings → Payment Accounts.',
      'Add your cash tills and bank accounts.',
      'These become the options staff pick when taking or making payments.',
    ],
    link: '/business-settings/finance/chart-of-accounts',
    linkLabel: 'Open Payment Accounts',
    keywords: ['payment accounts', 'bank', 'cash', 'till', 'accounts'],
  },
  {
    id: 'settings-tax',
    roles: OWNER_ADMIN,
    section: 'Business setup',
    title: 'Configure VAT & tax rates',
    summary: 'Set the tax rates applied across the app.',
    steps: [
      'Open Business Settings → Finance Settings → Business Tax Settings.',
      'Set your VAT rate and any other tax details and TINs.',
      'Save — these rates are used on invoices and returns.',
    ],
    link: '/business-settings/finance/tax',
    linkLabel: 'Open Tax Settings',
    keywords: ['tax settings', 'vat rate', 'tin', 'cit', 'tax config'],
  },

  // ─── Subscription & billing (owner only) ───────────────────────────────────
  {
    id: 'subscription-manage',
    roles: OWNER_ONLY,
    section: 'Subscription & billing',
    title: 'Manage your subscription & billing',
    summary: 'See your plan and change how you’re billed.',
    steps: [
      'Open Subscription from the countdown badge at the top, or the tenant menu.',
      'View your current plan and renewal date.',
      'Switch between monthly and annual billing.',
    ],
    link: '/subscription',
    linkLabel: 'Open Subscription',
    keywords: ['subscription', 'billing', 'plan', 'invoice', 'paystack'],
  },
  {
    id: 'subscription-upgrade',
    roles: OWNER_ONLY,
    section: 'Subscription & billing',
    title: 'Upgrade or renew your plan',
    summary: 'Move to a bigger plan or pay for another term.',
    steps: [
      'Open Subscription.',
      'Choose the plan you want (Starter, Growth or Pro).',
      'Pay securely with Paystack to activate it.',
      'Annual billing saves compared with paying monthly.',
    ],
    link: '/subscription',
    linkLabel: 'Open Subscription',
    keywords: ['upgrade', 'renew', 'plan', 'pay', 'starter growth pro'],
  },

  // ─── Your account ──────────────────────────────────────────────────────────
  {
    id: 'account-profile',
    roles: EVERYONE,
    section: 'Your account',
    title: 'Update your profile & password',
    summary: 'Change your own name, photo and password.',
    steps: [
      'Open My Profile from the settings area.',
      'Update your name or photo.',
      'Use the security section to change your password.',
    ],
    link: '/settings/profile',
    linkLabel: 'Open My Profile',
    keywords: ['profile', 'password', 'my account', 'change password'],
  },
  {
    id: 'account-notifications',
    roles: EVERYONE,
    section: 'Your account',
    title: 'Notifications',
    summary: 'Stay on top of alerts and reminders.',
    steps: [
      'Tap the bell at the top, or open System → Notifications.',
      'Tap a notification to open the related page and mark it read.',
      'Use Mark all read to clear the badge.',
    ],
    link: '/system/notifications',
    linkLabel: 'Open Notifications',
    keywords: ['notifications', 'alerts', 'bell', 'reminders'],
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

const KNOWN_ROLES: HelpRole[] = ['owner', 'admin', 'manager', 'cashier', 'accountant', 'sales_person', 'viewer']

/**
 * Normalise a raw session role string to a HelpRole for filtering.
 * Owner and the platform super_admin see everything. Any unrecognised custom
 * role falls back to the read-only `viewer` baseline so it still gets the
 * universal "everyone" articles without ever seeing higher-role topics.
 */
export function normalizeHelpRole(role?: string | null): HelpRole | 'super_admin' {
  const r = (role ?? '').toLowerCase().trim()
  if (r === 'owner') return 'owner'
  if (r === 'super_admin') return 'super_admin'
  if (KNOWN_ROLES.includes(r as HelpRole)) return r as HelpRole
  return 'viewer'
}

/** Articles visible to the given session role, in authoring order. */
export function articlesForRole(role?: string | null): HelpArticle[] {
  const r = normalizeHelpRole(role)
  if (r === 'owner' || r === 'super_admin') return HELP_ARTICLES
  return HELP_ARTICLES.filter(a => a.roles.includes(r))
}

/** Section headings (in first-seen order) for a set of articles. */
export function sectionsFor(articles: HelpArticle[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const a of articles) {
    if (!seen.has(a.section)) {
      seen.add(a.section)
      out.push(a.section)
    }
  }
  return out
}

/** Case-insensitive keyword filter across title, summary, steps and keywords. */
export function searchArticles(articles: HelpArticle[], query: string): HelpArticle[] {
  const q = query.trim().toLowerCase()
  if (!q) return articles
  const terms = q.split(/\s+/)
  return articles.filter(a => {
    const haystack = [
      a.title,
      a.summary,
      a.section,
      ...(a.steps ?? []),
      ...(a.keywords ?? []),
    ]
      .join(' ')
      .toLowerCase()
    return terms.every(t => haystack.includes(t))
  })
}

/**
 * Best-matching articles for the current page path — powers "Help on this page".
 * Matches an article's `link` as a prefix of the current pathname and returns
 * the longest (most specific) matches first, filtered to the given role.
 */
export function articlesForPath(pathname: string, role?: string | null): HelpArticle[] {
  const visible = articlesForRole(role)
  const scored = visible
    .filter(a => a.link && a.link !== '/' && pathIsRelated(pathname, a.link))
    .map(a => ({ a, len: (a.link as string).length }))
    .sort((x, y) => y.len - x.len)
  return scored.map(s => s.a)
}

/** True when `link` is the current page or a parent segment of it. */
function pathIsRelated(pathname: string, link: string): boolean {
  if (pathname === link) return true
  // Segment-aware prefix: '/sell' matches '/sell/invoices' but not '/selling'.
  return pathname.startsWith(link.endsWith('/') ? link : link + '/')
}
